/**
 * Firebase SDK configuration and custom Angular tokens
 * Replaces AngularFire with direct Firebase SDK imports for better control and Angular 22+ compatibility
 */
import { InjectionToken, inject, Provider, Injector } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import {
  initializeApp,
  getApp,
  FirebaseApp
} from 'firebase/app';
import {
  Auth,
  getAuth,
  initializeAuth,
  indexedDBLocalPersistence
} from 'firebase/auth';
import {
  Firestore,
  getFirestore,
  initializeFirestore
} from 'firebase/firestore';
import {
  Analytics,
  getAnalytics
} from 'firebase/analytics';
import {
  Messaging,
  getMessaging
} from 'firebase/messaging';
import {
  getStorage,
  FirebaseStorage
} from 'firebase/storage';
import { environment } from 'src/environments/environment';

// ============================================================================
// Custom InjectionTokens for Firebase services
// ============================================================================

export const FIREBASE_APP = new InjectionToken<FirebaseApp>('firebase.app');
export const FIREBASE_AUTH = new InjectionToken<Auth>('firebase.auth');
export const FIREBASE_FIRESTORE = new InjectionToken<Firestore>('firebase.firestore');
export const FIREBASE_ANALYTICS = new InjectionToken<Analytics>('firebase.analytics');
export const FIREBASE_MESSAGING = new InjectionToken<Messaging | null>('firebase.messaging');
export const FIREBASE_STORAGE = new InjectionToken<FirebaseStorage>('firebase.storage');

// ============================================================================
// Firebase App Provider
// ============================================================================

export function provideFirebaseSDK(): Provider[] {
  return [
    {
      provide: FIREBASE_APP,
      useFactory: () => {
        try {
          return getApp();
        } catch {
          return initializeApp(environment.firebaseConfig);
        }
      }
    },
    {
      provide: FIREBASE_AUTH,
      useFactory: () => {
        const app = inject(FIREBASE_APP);
        if (Capacitor.isNativePlatform()) {
          return initializeAuth(app, {
            persistence: indexedDBLocalPersistence,
          });
        }

        const auth = getAuth(app);

        // DEV ONLY (OPT-IN): Firebase Phone Auth testing mode.
        // When enabled, Firebase expects configured *test phone numbers* (no real SMS is sent).
        // Enable by running in DevTools console:
        //   localStorage.setItem('bebop_phoneauth_testing', '1'); location.reload();
        // Disable with:
        //   localStorage.removeItem('bebop_phoneauth_testing'); location.reload();
        if (!environment.production && typeof window !== 'undefined') {
          const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
          const testingEnabled = window.localStorage?.getItem('bebop_phoneauth_testing') === '1';
          if (isLocalhost && testingEnabled) {
            auth.settings.appVerificationDisabledForTesting = true;
          }
        }

        return auth;
      }
    },
    {
      provide: FIREBASE_FIRESTORE,
      useFactory: () => {
        const app = inject(FIREBASE_APP);
        if (Capacitor.isNativePlatform()) {
          // WKWebView blocks fetch calls to Firestore API; minimize persistence and force long polling to avoid CORS errors.
          return initializeFirestore(app, {
            cacheSizeBytes: 1048576, // Minimum allowed (1MB) to reduce persistence overhead
            experimentalAutoDetectLongPolling: false,
            experimentalForceLongPolling: true
          });
        }
        return getFirestore(app);
      }
    },
    {
      provide: FIREBASE_ANALYTICS,
      useFactory: () => {
        const app = inject(FIREBASE_APP);
        return getAnalytics(app);
      }
    },
    {
      provide: FIREBASE_MESSAGING,
      useFactory: () => {
        if (Capacitor.isNativePlatform()) {
          return null;
        }

        if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
          return null;
        }

        const app = inject(FIREBASE_APP);
        return getMessaging(app);
      }
    },
    {
      provide: FIREBASE_STORAGE,
      useFactory: () => {
        const app = inject(FIREBASE_APP);
        return getStorage(app);
      }
    }
  ];
}
